import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '@/lib/functions';
﻿import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Input } from '@/components/ui/Input';
import { Avatar } from '@/components/ui/Avatar';
import { LevelBadge, type LevelInfo } from '@/components/ui/LevelBadge';
import { ProviderBadge } from '@/components/ui/ProviderBadge';
import { StarRating } from '@/components/ui/StarRating';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import type { ReviewSummary } from '@/lib/database.types';
import { formatUserId, safeNum, circleShortName } from '@/lib/format';
import { getUserLevel } from '@/lib/levels';
import { searchProfiles, suggestProfiles, type SearchProfileResult } from '@/lib/profiles';
import { getReviewSummary } from '@/lib/reviews';
import { ErrorText } from '@/components/ErrorText';


export default function UserSearchScreen() {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { t } = useTranslation();
  const { user } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const { q } = useLocalSearchParams<{ q?: string }>();
  const [query, setQuery] = useState(q ?? '');
  const [results, setResults] = useState<SearchProfileResult[]>([]);
  const [suggestions, setSuggestions] = useState<SearchProfileResult[]>([]);
  const [summaries, setSummaries] = useState<Record<string, ReviewSummary>>({});
  const [levels, setLevels] = useState<Record<string, LevelInfo>>({});
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced αναζήτηση καθώς πληκτρολογεί ο χρήστης.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSuggestions([]);
      setSummaries({});
      setSearching(false);
      return;
    }
    setSearching(true);
    setError(null);
    const handle = setTimeout(async () => {
      try {
        const found = await searchProfiles(q, user?.id);
        setResults(found);
        // Αν δεν βρέθηκε τίποτα, ζήτα fuzzy προτάσεις («μήπως εννοείτε»).
        const sugg = found.length === 0 ? await suggestProfiles(q, user?.id) : [];
        setSuggestions(sugg);
        // Βαθμολογίες για όλα τα εμφανιζόμενα (παράλληλα).
        const all = [...found, ...sugg];
        const [sums, lvls] = await Promise.all([
          Promise.all(all.map((p) => getReviewSummary(p.id).catch(() => null))),
          Promise.all(all.map((p) => getUserLevel(p.id).catch(() => null))),
        ]);
        const map: Record<string, ReviewSummary> = {};
        const lvlMap: Record<string, LevelInfo> = {};
        all.forEach((p, i) => {
          if (sums[i]) map[p.id] = sums[i] as ReviewSummary;
          if (lvls[i]) lvlMap[p.id] = lvls[i] as LevelInfo;
        });
        setSummaries(map);
        setLevels(lvlMap);
      } catch (e) {
        setError(getErrorMessage(e, t('user.searchFailed')));
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query, user?.id]);

  const showHint = query.trim().length < 2;
  const showEmpty = !showHint && !searching && results.length === 0 && !error;

  const renderUser = useCallback((item: SearchProfileResult) => {
    const sum = summaries[item.id];
    const lvl = levels[item.id];
    const hasReviews = !!sum && safeNum(sum.review_count) > 0;
    return (
      <Pressable
        key={item.id}
        style={({ pressed }) => [styles.card, pressed && styles.cardPressed]}
        onPress={() => router.push({ pathname: '/(app)/users/[id]', params: { id: item.id } })}
        accessibilityRole="button"
      >
        <Avatar uri={item.avatar_url} name={item.full_name} size={44} />
        <View style={styles.cardText}>
          <View style={styles.nameRow}>
            <Text style={styles.name} numberOfLines={1}>
              {item.full_name?.trim() ? circleShortName(item.full_name, item.nickname) : t('common.user')}
            </Text>
          </View>
          <View style={styles.badgesRow}>
            {item.verified && <VerifiedBadge compact />}
            {lvl && <LevelBadge info={lvl} size="sm" />}
            {item.portfolio_enabled && item.portfolio_public && <ProviderBadge compact />}
          </View>
          {hasReviews ? (
            <View style={styles.ratingRow}>
              <StarRating value={safeNum(sum?.effective_rating)} size={13} />
              <Text style={styles.ratingCount}>({safeNum(sum?.review_count)})</Text>
            </View>
          ) : item.specialty?.trim() ? (
            <Text style={styles.bio} numberOfLines={1}>
              💼 {item.specialty}
            </Text>
          ) : !!item.bio?.trim() ? (
            <Text style={styles.bio} numberOfLines={1}>
              {item.bio}
            </Text>
          ) : null}
          <Text style={styles.idText}>{formatUserId(item.user_number)}</Text>
        </View>
        <Text style={styles.chevron}>›</Text>
      </Pressable>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaries, levels, styles, colors, router, t]);

  const renderItem = useCallback(({ item }: { item: SearchProfileResult }) => renderUser(item), [renderUser]);

  return (
    <View style={[styles.container, { paddingTop: insets.top + spacing.lg }]}>
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('search.title')}</Text>
        <View style={styles.spacer} />
      </View>

      <Input
        label={t('search.field_label')}
        value={query}
        onChangeText={setQuery}
        placeholder={t('placeholder.nameOrUserId')}
        autoCapitalize="words"
        autoCorrect={false}
        autoFocus
      />

      {searching && (
        <View style={styles.loaderRow}>
          <ActivityIndicator color={colors.primary} />
        </View>
      )}


      <ErrorText>{error}</ErrorText>

      <FlatList
        data={results}
        keyExtractor={(p) => p.id}
        contentContainerStyle={styles.list}
        keyboardShouldPersistTaps="handled"
        ListEmptyComponent={
          showHint ? (
            <Text style={styles.hint}>{t('search.hint')}</Text>
          ) : showEmpty ? (
            <View style={styles.emptyWrap}>
              <Text style={styles.hint}>{t('search.noResults')}</Text>
              {suggestions.length > 0 && (
                <>
                  <Text style={styles.suggestTitle}>{t('search.suggestions')}</Text>
                  {suggestions.map((s) => renderUser(s))}
                </>
              )}
            </View>
          ) : null
        }
        renderItem={renderItem}
      />
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    paddingHorizontal: spacing.lg,
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  back: {
    color: colors.primaryLight,
    fontSize: fontSize.md,
    fontWeight: '600',
    width: 70,
  },
  title: {
    color: colors.text,
    fontSize: fontSize.lg,
    fontWeight: '700',
  },
  spacer: { width: 70 },
  loaderRow: {
    paddingVertical: spacing.md,
  },
  list: {
    gap: spacing.sm,
    paddingVertical: spacing.md,
  },
  hint: {
    color: colors.textMuted,
    fontSize: fontSize.md,
    textAlign: 'center',
    paddingVertical: spacing.xl,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    minHeight: 68,
  },
  cardPressed: { opacity: 0.85 },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.surfaceAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: colors.text,
    fontSize: fontSize.lg,
    fontWeight: '700',
  },
  cardText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  nameRow: {
    flexDirection: 'row',
  },
  badgesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
  },
  name: {
    color: colors.text,
    fontSize: fontSize.md,
    fontWeight: '700',
    flexShrink: 1,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  ratingCount: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
  },
  nickname: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    fontStyle: 'italic',
  },
  bio: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
  },
  idText: {
    color: colors.textMuted,
    fontSize: fontSize.sm,
    fontWeight: '600',
    opacity: 0.7,
  },
  emptyWrap: {
    gap: spacing.sm,
    paddingTop: spacing.lg,
  },
  suggestTitle: {
    color: colors.text,
    fontSize: fontSize.md,
    fontWeight: '700',
    textAlign: 'center',
    marginTop: spacing.sm,
  },
  chevron: {
    color: colors.textMuted,
    fontSize: fontSize.xl,
  },
  error: {
    color: colors.danger,
    fontSize: fontSize.sm,
    textAlign: 'center',
    paddingVertical: spacing.sm,
  },
  });
}
